import { useCallback, useMemo, useState } from 'react';

export interface SelectableItem {
    id: string;
    createdAt?: number;
}

export type GridSelection =
    | { mode: 'explicit'; ids: ReadonlySet<string> }
    | {
          mode: 'all';
          selectedAt: number;
          total: number;
          excluded: ReadonlySet<string>;
      };

export interface GridMultiSelectionState {
    selection: GridSelection;
    selectedIds: Set<string>;
    isMultiSelectMode: boolean;
    selectedCount: number;
    isDescribed: boolean;
    isSelected: (item: SelectableItem) => boolean;
    toggleSelected: (id: string) => void;
    toggleItem: (item: SelectableItem) => void;
    selectAll: (ids: string[]) => void;
    selectAllMatching: (args: { selectedAt: number; total: number }) => void;
    deselect: (ids: string[]) => void;
    clearSelection: () => void;
    enterMultiSelectMode: () => void;
    exitMultiSelectMode: () => void;
}

const EMPTY: GridSelection = { mode: 'explicit', ids: new Set<string>() };

export function useGridMultiSelection(): GridMultiSelectionState {
    const [selection, setSelection] = useState<GridSelection>(EMPTY);
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

    const toggleSelected = useCallback((id: string) => {
        setSelection((previous) => {
            if (previous.mode === 'all') {
                const excluded = new Set(previous.excluded);
                if (excluded.has(id)) {
                    excluded.delete(id);
                } else {
                    excluded.add(id);
                }
                return { ...previous, excluded };
            }
            const ids = new Set(previous.ids);
            if (ids.has(id)) {
                ids.delete(id);
            } else {
                ids.add(id);
            }
            return { mode: 'explicit', ids };
        });
    }, []);

    const toggleItem = useCallback(
        (item: SelectableItem) => toggleSelected(item.id),
        [toggleSelected],
    );

    const selectAll = useCallback((ids: string[]) => {
        setSelection({ mode: 'explicit', ids: new Set(ids) });
    }, []);

    const selectAllMatching = useCallback(
        ({ selectedAt, total }: { selectedAt: number; total: number }) => {
            setSelection({
                mode: 'all',
                selectedAt,
                total,
                excluded: new Set<string>(),
            });
        },
        [],
    );

    const deselect = useCallback((ids: string[]) => {
        setSelection((previous) => {
            if (previous.mode === 'all') {
                const excluded = new Set(previous.excluded);
                ids.forEach((id) => excluded.add(id));
                return { ...previous, excluded };
            }
            const next = new Set(previous.ids);
            ids.forEach((id) => next.delete(id));
            return { mode: 'explicit', ids: next };
        });
    }, []);

    const clearSelection = useCallback(() => setSelection(EMPTY), []);

    const enterMultiSelectMode = useCallback(
        () => setIsMultiSelectMode(true),
        [],
    );

    const exitMultiSelectMode = useCallback(() => {
        setIsMultiSelectMode(false);
        setSelection(EMPTY);
    }, []);

    const isSelected = useCallback(
        (item: SelectableItem) => {
            if (selection.mode === 'explicit')
                return selection.ids.has(item.id);
            if (selection.excluded.has(item.id)) return false;
            return (
                item.createdAt === undefined ||
                item.createdAt <= selection.selectedAt
            );
        },
        [selection],
    );

    const selectedCount =
        selection.mode === 'explicit'
            ? selection.ids.size
            : Math.max(selection.total - selection.excluded.size, 0);

    const selectedIds = useMemo(
        () =>
            selection.mode === 'explicit'
                ? new Set(selection.ids)
                : new Set<string>(),
        [selection],
    );

    return {
        selection,
        selectedIds,
        isMultiSelectMode,
        selectedCount,
        isDescribed: selection.mode === 'all',
        isSelected,
        toggleSelected,
        toggleItem,
        selectAll,
        selectAllMatching,
        deselect,
        clearSelection,
        enterMultiSelectMode,
        exitMultiSelectMode,
    };
}
